import React from "react";
import {
  ShieldCheck,
  User,
  BarChart2,
  Share2,
  Lock,
  UserCheck,
  MessageSquare,
  AlertTriangle,
  Mail,
} from "lucide-react";
import NavPhc from "./NavPhc";
import privacyImageUrl from "../../assets/Privacy.jpg";

const PHCPrivacy = () => {
  // You can replace this with a real image path in your project

  return (
    <div>
      <NavPhc />
      <main className="bg-white text-gray-800">
        {/* --- Hero Section --- */}
        <section className="py-24 px-4 bg-[#0d1c5d] text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white">
            Privacy Policy
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white max-w-3xl mx-auto leading-8">
            Your trust is our top priority. This policy outlines how we collect,
            use, and protect your information when you use our healthcare
            platform.
          </p>
        </section>

        {/* --- Main Content Section --- */}
        <section className="py-24 sm:py-32">
          <div className="container mx-auto px-6 max-w-7xl">
            <div className="grid lg:grid-cols-3 gap-16 items-start">
              {/* Left Column: Text Content */}
              <div className="lg:col-span-2 space-y-10">
                {/* Introduction */}
                <div className="space-y-4">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    MNR Technologies Global Pvt Ltd is committed to protecting
                    your privacy. This Privacy Policy explains our practices
                    regarding the collection, use, and safeguarding of your
                    information through our healthcare services (the "Service").
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    By using our Service, you agree to the collection and use of
                    information in accordance with this policy.
                  </p>
                </div>

                {/* --- Core Patient Data Promise --- */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                  <div className="flex items-start gap-4">
                    <ShieldCheck className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h2 className="text-xl text-gray-900 font-bold mb-2">
                        Your Medical History is Sacred
                      </h2>
                      <p className="text-gray-700 leading-relaxed">
                        We want to be unequivocally clear: your personal medical
                        history and health data are not used in any shape or
                        form for any purpose other than your direct benefit.
                        This information is exclusively used to facilitate your
                        health checkups, provide personalized care, and support
                        your healthcare journey within our platform. It is never
                        sold, shared for marketing, or used for external
                        research without your explicit consent.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 1: Information We Collect */}
                <PolicySection icon={User} title="1. Information We Collect">
                  <p>
                    To provide and improve our Service, we collect the following
                    types of information:
                  </p>
                  <ul className="list-disc ml-6 mt-4 space-y-2 text-gray-600">
                    <li>
                      Personal Information: Name, email address, contact
                      details, and other identifiers you provide during
                      registration.
                    </li>
                    <li>
                      Usage Data: IP address, browser type, device information,
                      and how you interact with our Service (e.g., pages
                      visited, features used).
                    </li>
                    <li>
                      Cookies and Tracking Technologies: We use cookies to
                      enhance user experience and analyze service usage.
                    </li>
                  </ul>
                </PolicySection>

                {/* Section 2: How We Use Your Information */}
                <PolicySection
                  icon={BarChart2}
                  title="2. How We Use Your Information"
                >
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc ml-6 mt-4 space-y-2 text-gray-600">
                    <li>Provide, maintain, and improve our Service.</li>
                    <li>Process transactions and manage your account.</li>
                    <li>Send important service-related communications.</li>
                    <li>
                      Ensure the security of our platform and prevent fraud.
                    </li>
                    <li>Comply with legal and regulatory obligations.</li>
                  </ul>
                </PolicySection>

                {/* Section 3: Sharing Your Information */}
                <PolicySection
                  icon={Share2}
                  title="3. Sharing Your Information"
                >
                  <p>
                    We do not sell your personal data. We may share information
                    with:
                  </p>
                  <ul className="list-disc ml-6 mt-4 space-y-2 text-gray-600">
                    <li>
                      Service Providers: Trusted third parties who perform
                      services on our behalf (e.g., payment processors, cloud
                      hosting).
                    </li>
                    <li>
                      Legal Requirements: If required by law, such as in
                      response to a court order or subpoena.
                    </li>
                    <li>
                      Business Transfers: In connection with a merger,
                      acquisition, or sale of assets, with your consent where
                      required.
                    </li>
                  </ul>
                </PolicySection>

                {/* Section 4: Data Security */}
                <PolicySection icon={Lock} title="4. Data Security">
                  <p>
                    We implement robust technical and organizational measures
                    (such as encryption, access controls, and secure servers) to
                    protect your data. However, no method of transmission or
                    storage is 100% secure, and we cannot guarantee absolute
                    security.
                  </p>
                </PolicySection>

                {/* Section 5: Your Rights */}
                <PolicySection icon={UserCheck} title="5. Your Rights">
                  <p>
                    You have the right to access, update, or delete your
                    personal data. You may also have other rights depending on
                    your location, such as the right to restrict processing or
                    withdraw consent. To exercise these rights, please contact
                    us.
                  </p>
                </PolicySection>

                {/* Section 6: Children's Privacy */}
                <PolicySection
                  icon={AlertTriangle}
                  title="6. Children's Privacy"
                >
                  <p>
                    Our Service is not intended for individuals under the age of
                    18. We do not knowingly collect personal data from children.
                    If we become aware that we have, we will take steps to
                    delete such information.
                  </p>
                </PolicySection>

                {/* Section 7: Changes to This Policy */}
                <PolicySection
                  icon={MessageSquare}
                  title="7. Changes to This Policy"
                >
                  <p>
                    We may update this Privacy Policy from time to time. We will
                    notify you of any changes by posting the new policy on this
                    page and updating the "Last Updated" date.
                  </p>
                </PolicySection>
              </div>

              {/* Right Column: Image and Contact */}
              <aside className="lg:col-span-1 sticky top-24">
                <img
                  src={privacyImageUrl}
                  alt="A secure lock symbolizing data privacy"
                  className="w-full h-auto object-cover rounded-2xl shadow-lg mb-8"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://placehold.co/600x400";
                  }}
                />
                <div className="bg-slate-50 p-6 rounded-2xl border border-gray-200">
                  <div className="flex items-start gap-4">
                    <Mail className="h-7 w-7 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl text-gray-900 font-bold mb-2">
                        Contact Us
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        If you have any questions about this Privacy Policy,
                        please contact us at:
                        <br />
                        <a
                          href="mailto:compliance@mnrtechnologies.com"
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          compliance@mnrtechnologies.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

// Helper component for consistent section styling
const PolicySection = ({ icon: Icon, title, children }) => {
  return (
    <div className="border-t border-gray-200 pt-8">
      <div className="flex items-start gap-4">
        <Icon className="h-7 w-7 text-blue-600 flex-shrink-0 mt-1" />
        <div className="flex-grow">
          <h2 className="text-2xl text-gray-900 font-bold mb-4">{title}</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PHCPrivacy;
